export interface ChatZeroStateSession {
  id: string;
  name: string;
}

export interface ChatZeroStateProps {
  /** The persona this chat will run as, so the user knows who they are talking to. */
  personaName?: string;
  personaDescription?: string;
  starterPrompts?: string[];
  recentSessions?: ChatZeroStateSession[];
  onSendMessage: (content: string) => void;
  onOpenSession?: (sessionId: string) => void;
}

export function ChatZeroState({
  personaName,
  personaDescription,
  starterPrompts = [],
  recentSessions = [],
  onSendMessage,
  onOpenSession,
}: ChatZeroStateProps) {
  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-6 py-12" data-testid="chat-zero-state">
      <div className="space-y-1.5">
        <h2 className="text-lg font-semibold" data-testid="chat-zero-state-persona">
          {personaName ? `You're chatting with ${personaName}` : 'Start a conversation'}
        </h2>
        {personaDescription ? (
          <p className="text-sm text-muted-foreground">{personaDescription}</p>
        ) : null}
      </div>

      {starterPrompts.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium uppercase text-muted-foreground">Try asking</p>
          <div className="flex flex-col gap-2">
            {starterPrompts.map((prompt) => (
              <button
                key={prompt}
                type="button"
                data-testid="chat-starter-prompt"
                onClick={() => onSendMessage(prompt)}
                className="rounded-md px-3 py-2 text-left text-sm transition-colors hover:bg-accent"
              >
                {prompt}
              </button>
            ))}
          </div>
        </div>
      )}

      {recentSessions.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium uppercase text-muted-foreground">Pick up where you left off</p>
          <div className="flex flex-col gap-1">
            {recentSessions.map((session) => (
              <button
                key={session.id}
                type="button"
                data-testid="chat-recent-session"
                data-session-id={session.id}
                onClick={() => onOpenSession?.(session.id)}
                className="rounded-md px-2 py-1.5 text-left text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
              >
                {session.name}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
